/**
 * Die Mandantenakte. Links bleiben die Mandanten stehen, rechts die Akte:
 * Bankkonten und die Auszuege je Konto in zeitlicher Folge. Die erzeugten
 * Dateien stehen nicht hier, sondern gesammelt unter Exporte.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiFehler, type Bankkonto, type Mandantenakte, type MandantZeile } from '../lib/api'
import { euro, iban, karteKurz, STAND_TEXT, zeitraum } from '../lib/format'
import { Filterfeld } from '../teile/Filterfeld'
import { Fenster } from '../teile/Fenster'
import { Exportieren } from '../teile/Exportieren'
import { KartenExportieren } from '../teile/KartenExportieren'
import { KartenStamm } from '../teile/KartenStamm'
import { KartenStammHinweis } from '../teile/KartenStammHinweis'
import { NUR_CAMT } from '../lib/karte'
import { artParameter, pfad, useArt } from '../lib/art'

const auszugSpaltenWahl = { gridTemplateColumns: '32px minmax(0, 1fr) 215px 100px 100px 80px 105px' }

/** Der Kreis am Zeilenanfang, wie im Eingang. */
function Wahlkreis({ gewaehlt, gesperrt, titel }: { gewaehlt: boolean; gesperrt?: boolean; titel: string }) {
  return (
    <span className={`wahlkreis${gewaehlt ? ' gewaehlt' : ''}${gesperrt ? ' gesperrt' : ''}`} role="checkbox" aria-checked={gewaehlt} aria-disabled={gesperrt} aria-label={titel}>
      {gewaehlt && (
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M2.5 6.5 5 9l4.5-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  )
}

const SPALTE_SPEICHER = 'konto.mandantenspalte'

export function MandantSeite() {
  const { id } = useParams()
  const gehZu = useNavigate()
  const art = useArt()
  const karte = art === 'kreditkarte'
  const [suche, setzeSuche] = useState('')
  const [spalteOffen, setzeSpalteOffen] = useState(() => {
    try {
      return localStorage.getItem(SPALTE_SPEICHER) !== 'zu'
    } catch {
      return true
    }
  })
  const [exportOffen, setzeExportOffen] = useState(false)
  const [kartenExportOffen, setzeKartenExportOffen] = useState(false)
  const [loeschenOffen, setzeLoeschenOffen] = useState(false)
  const [wahlmodus, setzeWahlmodus] = useState(false)
  const [gewaehlt, setzeGewaehlt] = useState<Set<string>>(new Set())
  const [auszuegeLoeschenOffen, setzeAuszuegeLoeschenOffen] = useState(false)
  const [loescht, setzeLoescht] = useState(false)
  const [meldung, setzeMeldung] = useState<{ art: 'gut' | 'schlecht'; text: string } | null>(null)
  const abfragen = useQueryClient()

  useEffect(() => {
    try {
      localStorage.setItem(SPALTE_SPEICHER, spalteOffen ? 'offen' : 'zu')
    } catch {
      /* ohne Speicher klappt es trotzdem */
    }
  }, [spalteOffen])

  const liste = useQuery({
    queryKey: ['mandanten', art, suche],
    queryFn: () => api.holen<{ mandanten: MandantZeile[] }>(`/api/mandanten?${artParameter(art)}${suche ? `&suche=${encodeURIComponent(suche)}` : ''}`),
  })
  const akte = useQuery({
    queryKey: ['mandant', id],
    queryFn: () => api.holen<Mandantenakte>(`/api/mandanten/${id}`),
    enabled: Boolean(id),
  })

  const a = akte.data
  // Die Akte liefert alle Auszuege des Mandanten, gezeigt werden die dieser
  // Art. Einer mit unbestaetigter Art steht auf beiden Seiten.
  const auszuege = (a?.auszuege ?? []).filter((x) => x.art === art || x.art_unsicher)
  const bereit = auszuege.filter((x) => x.stand === 'bereit' || x.stand === 'freigegeben').length
  const zuPruefen = auszuege.filter((x) => x.stand === 'pruefen' || x.stand === 'doppelt').length

  /* ── Auswahl zum Loeschen, wie im Eingang. Exportierte sind nicht waehlbar. */
  const waehlbar = auszuege.filter((x) => x.stand !== 'exportiert').map((x) => x.id)
  function umschalten(idAuszug: string) {
    setzeGewaehlt((alt) => {
      const neu = new Set(alt)
      if (neu.has(idAuszug)) neu.delete(idAuszug)
      else neu.add(idAuszug)
      return neu
    })
  }
  function auswahlBeenden() {
    setzeWahlmodus(false)
    setzeGewaehlt(new Set())
  }
  async function gewaehlteLoeschen() {
    setzeLoescht(true)
    try {
      const ergebnis = await api.senden<{ auszuege: number; dokumente: number }>('/api/auszuege/loeschen', { auszuege: [...gewaehlt], dokumente: [] })
      setzeMeldung({ art: 'gut', text: `${ergebnis.auszuege === 1 ? 'Ein Auszug' : `${ergebnis.auszuege} Auszüge`} gelöscht${ergebnis.dokumente > 0 ? `, ${ergebnis.dokumente === 1 ? 'ein Original' : `${ergebnis.dokumente} Originale`} mit entfernt` : ''}.` })
      setzeAuszuegeLoeschenOffen(false)
      auswahlBeenden()
      await abfragen.invalidateQueries()
    } catch (problem) {
      setzeMeldung({ art: 'schlecht', text: problem instanceof ApiFehler ? problem.message : 'Das Löschen hat nicht geklappt.' })
      setzeAuszuegeLoeschenOffen(false)
    } finally {
      setzeLoescht(false)
    }
  }
  // Die Kreisspalte ist immer da, damit nichts springt: der Kreis erscheint
  // beim Ueberfahren, ein Klick darauf beginnt die Auswahl.
  const raster = auszugSpaltenWahl
  const [filter, setzeFilter] = useState<'alle' | 'bereit' | 'pruefen'>('alle')
  const sichtbar = auszuege.filter((x) =>
    filter === 'alle' ? true : filter === 'bereit' ? x.stand === 'bereit' || x.stand === 'freigegeben' : x.stand === 'pruefen' || x.stand === 'doppelt',
  )

  return (
    <div className={`zweispaltig${spalteOffen ? '' : ' spalte-zu'}`}>
      <aside className="mandantenspalte">
        <button type="button" className="spaltenschalter" onClick={() => setzeSpalteOffen(!spalteOffen)} aria-label={spalteOffen ? 'Mandanten ausblenden' : 'Mandanten einblenden'}>
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d={spalteOffen ? 'M7.5 2.5 4 6l3.5 3.5' : 'M4.5 2.5 8 6 4.5 9.5'} fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>
        </button>
        <Filterfeld wert={suche} setzeWert={setzeSuche} beschriftung="Mandanten filtern" />
        <div className="mandantenzeilen">
          {liste.data?.mandanten.map((m) => (
            <Link key={m.id} to={pfad(art, `/mandanten/${m.id}`)} className={`mandantenzeile${m.id === id ? ' aktiv' : ''}`}>
              <span className="sache">{m.name}</span>
              {m.zu_pruefen > 0 && <span className="nebensache text-warnung">{m.zu_pruefen} zu prüfen</span>}
            </Link>
          ))}
        </div>
      </aside>

      <section className="akte">
        {akte.isPending && <div className="laedt">Wird geladen …</div>}
        {akte.error && <div className="fehler">{akte.error instanceof Error ? akte.error.message : 'Die Akte konnte nicht geladen werden.'}</div>}
        {a && (
          <>
            {/* ── Kopf wie im JA-Werkzeug: der Name ist der Aufklapper, dahinter
                   die Stammdaten als Raster. Zugeklappt sieht man nur den Namen
                   und die Liste. Bearbeiten liegt bei den Stammdaten. */}
            <div className="akte-kopf">
              <details className="stammdaten">
                <summary title="Stammdaten anzeigen">
                  <h1>{a.mandant.name}</h1>
                  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" className="winkel">
                    <path d="M6.5 4 L11.5 9 L6.5 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {/* Nur auf der Kreditkartenseite: sagt, was hinter dem Winkel liegt. */}
                  {karte && <span className="stammdaten-wort">Stammdaten und Karten</span>}
                </summary>
                {/* Aufgeklappt ist die Akte im Bearbeitungsmodus: Name, Nummer,
                    Konten, unten still das Loeschen. Kein zweites Fenster. */}
                {/* Kreditkartenseite: eigenes Formular (Stammdaten des Buchungsstapels
                    mit einem Knopf, Karten statt Bankkonten). Kontoauszuege unveraendert. */}
                {karte ? (
                  <KartenStamm key={a.mandant.id} mandant={a.mandant} loeschbar={a.auszuege.length === 0} aufLoeschen={() => setzeLoeschenOffen(true)} />
                ) : (
                  <MandantFormular
                    key={a.mandant.id}
                    mandant={a.mandant}
                    konten={a.konten}
                    loeschbar={a.auszuege.length === 0}
                    aufLoeschen={() => setzeLoeschenOffen(true)}
                  />
                )}
              </details>
            </div>
            {karte && !NUR_CAMT && <KartenStammHinweis mandantId={a.mandant.id} mandantennummer={a.mandant.mandantennummer} />}

            {/* ── Auszüge ────────────────────────────────────────── */}
            <div className="listenleiste akte-leiste">
              {wahlmodus ? (
                <div className="auswahlleiste" role="group" aria-label="Auswahl">
                  {gewaehlt.size === waehlbar.length
                    ? <button type="button" className="knopf klein" onClick={() => setzeGewaehlt(new Set())}>Keine auswählen</button>
                    : <button type="button" className="knopf klein" onClick={() => setzeGewaehlt(new Set(waehlbar))}>Alle auswählen</button>}
                  {gewaehlt.size > 0 && <span className="leise">{gewaehlt.size === 1 ? '1 ausgewählt' : `${gewaehlt.size} ausgewählt`}</span>}
                </div>
              ) : (
                <div className="wahl" role="group" aria-label="Anzeige">
                  <button type="button" aria-pressed={filter === 'alle'} onClick={() => setzeFilter('alle')}>Alle <span className="zahl">{auszuege.length}</span></button>
                  {bereit > 0 && <button type="button" aria-pressed={filter === 'bereit'} onClick={() => setzeFilter('bereit')}>Bereit <span className="zahl">{bereit}</span></button>}
                  {zuPruefen > 0 && <button type="button" aria-pressed={filter === 'pruefen'} onClick={() => setzeFilter('pruefen')}>Zu prüfen <span className="zahl">{zuPruefen}</span></button>}
                </div>
              )}
              <div className="leiste-rechts">
                {wahlmodus ? (
                  <>
                    <button type="button" className="knopf still loeschknopf" disabled={gewaehlt.size === 0} onClick={() => setzeAuszuegeLoeschenOffen(true)}>Löschen</button>
                    <button type="button" className="knopf still" onClick={auswahlBeenden}>Fertig</button>
                  </>
                ) : karte ? (
                  <button type="button" className="knopf" disabled={bereit === 0} onClick={() => setzeKartenExportOffen(true)}>
                    Für DATEV exportieren
                  </button>
                ) : (
                  <button type="button" className="knopf" disabled={karte || bereit === 0} title={karte ? 'Der Export für Kreditkarten folgt, sobald ihre Buchungen ausgelesen werden.' : undefined} onClick={() => setzeExportOffen(true)}>
                    Für DATEV exportieren
                  </button>
                )}
              </div>
            </div>
            {meldung && <div className={`${meldung.art === 'gut' ? 'bestaetigung' : 'fehler'} abstand-unten-klein`}>{meldung.text}</div>}

            <div className="liste">
              {auszuege.length > 0 && (
              <div className="listenkopf" style={raster}>
                <div />
                <div>{karte ? 'Abrechnung' : 'Auszug'}</div>
                <div>{karte ? 'Karte' : 'Konto'}</div>
                <div className="rechts">Anfang</div>
                <div className="rechts">Schluss</div>
                <div className="rechts">Buchungen</div>
                <div>Stand</div>
              </div>
              )}
              {auszuege.length === 0 && (
                <div className="leer">
                  {karte ? (
                    <>
                      <strong>Noch keine Kreditkartenabrechnungen.</strong>
                      <p>Hochgeladene Abrechnungen landen hier, sobald sie diesem Mandanten zugeordnet sind.</p>
                    </>
                  ) : (
                    <>
                      <strong>Noch keine Auszüge.</strong>
                      <p>Hochgeladene Auszüge landen hier, sobald ihre IBAN zu einem Konto dieses Mandanten gehört.</p>
                    </>
                  )}
                </div>
              )}
              {sichtbar.map((x) => {
                const istWaehlbar = x.stand !== 'exportiert'
                const istGewaehlt = gewaehlt.has(x.id)
                const kreis = (
                  <div
                    className={`kreiszelle${istWaehlbar ? '' : ' gesperrt'}`}
                    onClick={istWaehlbar ? (e) => { e.preventDefault(); e.stopPropagation(); if (!wahlmodus) setzeWahlmodus(true); umschalten(x.id) } : undefined}
                  >
                    <Wahlkreis gewaehlt={istGewaehlt} gesperrt={!istWaehlbar} titel={x.auszug_nummer ? `Auszug ${x.auszug_nummer}` : 'Auszug'} />
                  </div>
                )
                const inhalt = (
                  <>
                    <div>
                      <div className="sache">{x.art === 'kreditkarte' ? 'Kreditkartenabrechnung' : x.auszug_nummer ? `Auszug ${x.auszug_nummer}` : 'Auszug'}{x.art_unsicher && <span className="marke warnung">Art prüfen</span>}</div>
                      {x.bank && <div className="nebensache gekuerzt" title={x.bank}>{x.bank}</div>}
                    </div>
                    <div>
                      <div className="leise zahl einzeilig">{x.art === 'kreditkarte' ? karteKurz(x.art_beleg) : iban(x.iban)}</div>
                      <div className="nebensache zahl">{zeitraum(x.zeitraum_von, x.zeitraum_bis)}</div>
                    </div>
                    <div className="leise zahl rechts">{euro(x.anfangssaldo_cent)}</div>
                    <div className="leise zahl rechts">{euro(x.schlusssaldo_cent)}</div>
                    <div className="leiser zahl rechts">{x.buchungen}</div>
                    <div><span className={`stand ${x.stand}`}>{STAND_TEXT[x.stand]}</span></div>
                  </>
                )
                if (wahlmodus) {
                  return (
                    <div key={x.id} className={`zeile mit-kreis${istWaehlbar ? ' zeile-wahl' : ''}${istGewaehlt ? ' gewaehlt' : ''}`} style={raster} onClick={istWaehlbar ? () => umschalten(x.id) : undefined}>
                      {kreis}
                      {inhalt}
                    </div>
                  )
                }
                return (
                  <Link key={x.id} to={`/auszuege/${x.id}`} state={{ von: pfad(art, `/mandanten/${a.mandant.id}`) }} className="zeile mit-kreis" style={raster}>
                    {kreis}
                    {inhalt}
                  </Link>
                )
              })}
              {auszuege.length > 0 && sichtbar.length === 0 && <div className="leer"><strong>Nichts in dieser Auswahl.</strong></div>}
            </div>

            <Fenster titel={gewaehlt.size === 1 ? 'Auszug löschen' : `${gewaehlt.size} Auszüge löschen`} offen={auszuegeLoeschenOffen} aufSchliessen={() => { if (!loescht) setzeAuszuegeLoeschenOffen(false) }}>
              <div className="formular">
                <p className="leise">
                  {gewaehlt.size === 1 ? 'Der gewählte Auszug wird' : `Die ${gewaehlt.size} gewählten Auszüge werden`} mit allen Buchungen gelöscht.
                  Ein Original, zu dem danach kein Auszug mehr gehört, wird mit entfernt. Exportierte Auszüge sind nicht betroffen.
                </p>
                <div className="formular-fuss">
                  <button type="button" className="knopf still" onClick={() => setzeAuszuegeLoeschenOffen(false)} disabled={loescht}>Abbrechen</button>
                  <button type="button" className="knopf gefuellt loeschknopf" onClick={() => void gewaehlteLoeschen()} disabled={loescht}>{loescht ? 'Wird gelöscht …' : 'Löschen'}</button>
                </div>
              </div>
            </Fenster>

            {exportOffen && <Exportieren mandantId={a.mandant.id} offen aufSchliessen={() => setzeExportOffen(false)} />}
            {kartenExportOffen && <KartenExportieren mandantId={a.mandant.id} offen aufSchliessen={() => setzeKartenExportOffen(false)} />}

            <MandantLoeschen
              mandantId={a.mandant.id}
              name={a.mandant.name}
              offen={loeschenOffen}
              aufSchliessen={() => setzeLoeschenOffen(false)}
              aufGeloescht={() => gehZu(pfad(art, '/mandanten'))}
            />
          </>
        )}
      </section>
    </div>
  )
}

/**
 * Einen Mandanten loeschen. Steht nur bereit, solange kein Auszug an ihm
 * haengt. Der Server prueft zusaetzlich auf Exporte, damit keine Exportspur
 * ohne Eigentuemer zurueckbleibt.
 */
function MandantLoeschen({ mandantId, name, offen, aufSchliessen, aufGeloescht }: {
  mandantId: string; name: string; offen: boolean; aufSchliessen: () => void; aufGeloescht: () => void
}) {
  const abfragen = useQueryClient()
  const [fehler, setzeFehler] = useState<string | null>(null)
  const [laeuft, setzeLaeuft] = useState(false)

  async function loeschen() {
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      await api.loeschen(`/api/mandanten/${mandantId}`)
      await abfragen.invalidateQueries()
      aufSchliessen()
      aufGeloescht()
    } catch (problem) {
      setzeFehler(problem instanceof ApiFehler ? problem.message : 'Der Mandant konnte nicht gelöscht werden.')
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <Fenster titel="Mandant löschen" offen={offen} aufSchliessen={aufSchliessen}>
      <div className="formular">
        {fehler && <div className="fehler">{fehler}</div>}
        <p className="leise"><span className="klein-sache">{name}</span> wird mit seinen Bankkonten gelöscht.</p>
        <p className="leiser">Sobald ein Auszug oder ein Export daranhängt, geht das nicht mehr. Dann bleibt der Mandant bestehen, damit die Kette nachvollziehbar bleibt.</p>
        <div className="formular-fuss">
          <button type="button" className="knopf still" onClick={aufSchliessen}>Abbrechen</button>
          <button type="button" className="knopf gefuellt loeschknopf" disabled={laeuft} onClick={() => void loeschen()}>{laeuft ? 'Wird gelöscht …' : 'Löschen'}</button>
        </div>
      </div>
    </Fenster>
  )
}

/**
 * Das Bearbeitungsformular der Akte, direkt unter dem Namen: Name und
 * Mandantennummer werden mit "Speichern" geschrieben. Konten wirken sofort,
 * weil jedes Konto ein eigener Datensatz ist (hinzufuegen, Bank aendern,
 * entfernen). Unten links, still, das Loeschen.
 */
function MandantFormular({ mandant, konten, loeschbar, aufLoeschen }: {
  mandant: { id: string; name: string; mandantennummer: number | null }; konten: Bankkonto[]; loeschbar: boolean; aufLoeschen: () => void
}) {
  const abfragen = useQueryClient()
  const [name, setzeName] = useState(mandant.name)
  const [nummer, setzeNummer] = useState(mandant.mandantennummer ? String(mandant.mandantennummer) : '')
  const [neuIban, setzeNeuIban] = useState('')
  const [neuBank, setzeNeuBank] = useState('')
  const [entfernenFrage, setzeEntfernenFrage] = useState<string | null>(null)
  const [fehler, setzeFehler] = useState<string | null>(null)
  const [gespeichert, setzeGespeichert] = useState(false)
  const [laeuft, setzeLaeuft] = useState(false)
  const geaendert = name.trim() !== mandant.name || (nummer.trim() ? Number(nummer.trim()) : null) !== mandant.mandantennummer

  async function tun(arbeit: () => Promise<unknown>, meldung: string) {
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      await arbeit()
      await abfragen.invalidateQueries()
      return true
    } catch (problem) {
      setzeFehler(problem instanceof ApiFehler ? problem.message : meldung)
      return false
    } finally {
      setzeLaeuft(false)
    }
  }

  async function speichern(e: FormEvent) {
    e.preventDefault()
    const ok = await tun(
      () => api.aendern(`/api/mandanten/${mandant.id}`, { name: name.trim(), mandantennummer: nummer.trim() ? Number(nummer.trim()) : null }),
      'Der Mandant konnte nicht gespeichert werden.',
    )
    if (ok) {
      setzeGespeichert(true)
      setTimeout(() => setzeGespeichert(false), 2500)
    }
  }
  async function kontoHinzufuegen() {
    if (!neuIban.trim()) return
    const ok = await tun(() => api.senden(`/api/mandanten/${mandant.id}/konten`, { iban: neuIban, bank: neuBank.trim() || null }), 'Das Konto konnte nicht angelegt werden.')
    if (ok) { setzeNeuIban(''); setzeNeuBank('') }
  }
  async function bankSpeichern(k: Bankkonto, bank: string) {
    if ((bank.trim() || null) === (k.bank ?? null)) return
    await tun(() => api.aendern(`/api/konten/${k.id}`, { bank: bank.trim() || null }), 'Das Konto konnte nicht gespeichert werden.')
  }
  async function kontoEntfernen(k: Bankkonto) {
    const ok = await tun(() => api.loeschen(`/api/konten/${k.id}`), 'Das Konto konnte nicht entfernt werden.')
    if (ok) setzeEntfernenFrage(null)
  }

  return (
    <form className="formular akte-formular" onSubmit={(e) => void speichern(e)}>
      {fehler && <div className="fehler">{fehler}</div>}
      <div className="formular-reihe">
        <div><label htmlFor="mb-name">Name</label><input id="mb-name" type="text" required autoComplete="off" data-1p-ignore value={name} onChange={(e) => setzeName(e.target.value)} /></div>
        <div><label htmlFor="mb-nummer">Mandantennummer in DATEV</label><input id="mb-nummer" type="text" inputMode="numeric" className="zahl" autoComplete="off" data-1p-ignore value={nummer} onChange={(e) => setzeNummer(e.target.value.replace(/[^0-9]/g, ''))} /></div>
      </div>

      <div className="abschnitt-kopf abstand-oben-klein"><h2>Bankkonten</h2></div>
      <div className="kontenliste">
        {konten.length === 0 && <p className="leiser">Noch kein Konto. Es entsteht beim ersten Zuordnen eines Auszugs von selbst.</p>}
        {konten.map((k) => (
          <div key={k.id} className="kontozeile bearbeitbar">
            <span className="zahl einzeilig">{iban(k.iban)}</span>
            <input type="text" aria-label="Bank" placeholder="Bank" autoComplete="off" data-1p-ignore defaultValue={k.bank ?? ''} onBlur={(e) => void bankSpeichern(k, e.target.value)} />
            {entfernenFrage === k.id ? (
              <span className="rueckfrage">
                Wirklich?
                <button type="button" className="knopf klein loeschknopf" disabled={laeuft} onClick={() => void kontoEntfernen(k)}>Entfernen</button>
                <button type="button" className="knopf still klein" onClick={() => setzeEntfernenFrage(null)}>Nein</button>
              </span>
            ) : (
              <button type="button" className="knopf still klein" onClick={() => setzeEntfernenFrage(k.id)}>Entfernen</button>
            )}
          </div>
        ))}
        <div className="kontozeile bearbeitbar neu">
          <input type="text" className="zahl" aria-label="Neue IBAN" placeholder="IBAN" autoComplete="off" data-1p-ignore value={neuIban} onChange={(e) => setzeNeuIban(e.target.value)} />
          <input type="text" aria-label="Bank" placeholder="Bank" autoComplete="off" data-1p-ignore value={neuBank} onChange={(e) => setzeNeuBank(e.target.value)} />
          <button type="button" className="knopf klein" disabled={laeuft || !neuIban.trim()} onClick={() => void kontoHinzufuegen()}>Hinzufügen</button>
        </div>
      </div>
      <div className="hinweis">Auszüge mit einer dieser IBANs ordnen sich diesem Mandanten von selbst zu. Konten wirken sofort, Name und Nummer mit Speichern.</div>

      <div className="formular-fuss">
        {loeschbar ? (
          <button type="button" className="knopf still links loeschknopf" onClick={aufLoeschen}>Mandant löschen</button>
        ) : (
          <span className="links leiser">Löschen geht erst, wenn kein Auszug mehr an diesem Mandanten hängt.</span>
        )}
        {gespeichert && <span className="leise">Gespeichert.</span>}
        <button type="submit" className="knopf gefuellt" disabled={laeuft || !geaendert}>{laeuft ? 'Wird gespeichert …' : 'Speichern'}</button>
      </div>
    </form>
  )
}

